import {
  ArrowLeftOutlined,
  DeleteOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  PlusOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  message
} from 'antd';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SendCandidateCallModal from '../components/CandidateSearch/SendCandidateCallModal';
import SendCandidateEmailModal from '../components/CandidateSearch/SendCandidateEmailModal';
import SendCandidateWhatsAppModal from '../components/CandidateSearch/SendCandidateWhatsAppModal';
import AppLayout from '../metronic/layouts/AppLayout';

const { Title, Text, Paragraph } = Typography;
const MAX_ENRICH = 25;

const authHeader = async () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

export default function CandidateLists() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(() => Boolean(listId));
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [selectedIds, setSelectedIds] = useState([]);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichResults, setEnrichResults] = useState(null);
  const [emailCandidate, setEmailCandidate] = useState(null);
  const [callCandidate, setCallCandidate] = useState(null);
  const [whatsappCandidate, setWhatsappCandidate] = useState(null);

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/candidate-lists', {
        headers: await authHeader(),
        params: { limit: 80 }
      });
      if (res.data?.success) {
        setLists(res.data.lists || []);
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load lists');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    setSelectedIds([]);
    try {
      const res = await axios.get(`/api/candidate-lists/${encodeURIComponent(id)}`, {
        headers: await authHeader()
      });
      if (res.data?.success) {
        setDetail(res.data.list);
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load list');
      navigate('/candidate-lists');
    } finally {
      setDetailLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    if (listId) {
      setDetailLoading(true);
      setDetail(null);
      loadDetail(listId);
    } else {
      setDetail(null);
      setDetailLoading(false);
    }
  }, [listId, loadDetail]);

  const onCreate = async () => {
    try {
      const v = await createForm.validateFields();
      const res = await axios.post(
        '/api/candidate-lists',
        { name: v.name, description: v.description || undefined },
        { headers: await authHeader() }
      );
      if (res.status === 201 && res.data?.list_id) {
        message.success('Candidate list created');
        setCreateOpen(false);
        createForm.resetFields();
        await loadLists();
        navigate(`/candidate-lists/${res.data.list_id}`);
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.error || 'Create failed');
    }
  };

  const onDeleteList = async (id) => {
    try {
      await axios.delete(`/api/candidate-lists/${encodeURIComponent(id)}`, {
        headers: await authHeader()
      });
      message.success('List deleted (candidate profiles unchanged)');
      if (listId === id) navigate('/candidate-lists');
      loadLists();
      setDetail(null);
    } catch (e) {
      message.error(e.response?.data?.error || 'Delete failed');
    }
  };

  const openEnrich = () => {
    if (selectedIds.length < 1) {
      message.warning('Select at least one member (max 25)');
      return;
    }
    setEnrichResults(null);
    setEnrichOpen(true);
  };

  const runEnrich = async () => {
    setEnrichLoading(true);
    setEnrichResults(null);
    try {
      const res = await axios.post(
        '/api/candidates/research-bulk',
        {
          candidate_ids: selectedIds.slice(0, MAX_ENRICH),
          list_id: listId || undefined
        },
        { headers: await authHeader() }
      );
      if (res.data?.success) {
        setEnrichResults(res.data);
        message.success(
          `Enrichment finished: ${res.data.summary?.succeeded ?? 0} ok, ${res.data.summary?.failed ?? 0} failed`
        );
      } else {
        message.error(res.data?.error || 'Bulk enrichment failed');
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Bulk enrichment failed');
    } finally {
      setEnrichLoading(false);
    }
  };

  const openCandidateProfile = (candidate) => {
    const candidateId = candidate?.id || candidate?.candidate_id;
    if (!candidateId || candidate?.missing) return;
    const returnTo = `/candidate-lists/${listId}`;
    navigate(
      `/all-candidates?candidate_id=${encodeURIComponent(candidateId)}&return_to=${encodeURIComponent(returnTo)}`
    );
  };

  if (listId && detail) {
    const members = detail.members || [];
    const rowSelection = {
      selectedRowKeys: selectedIds,
      onChange: (keys) => {
        if (keys.length > MAX_ENRICH) {
          message.warning(`You can select at most ${MAX_ENRICH} candidates per run`);
          return;
        }
        setSelectedIds(keys);
      },
      getCheckboxProps: (record) => ({
        disabled: record.missing === true
      })
    };

    return (
      <AppLayout>
        <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/candidate-lists')}>
              All candidate lists
            </Button>
            <div>
              <Title level={3} style={{ marginBottom: 0 }}>{detail.name}</Title>
              {detail.description ? <Text type="secondary">{detail.description}</Text> : null}
              <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                Enrichment uses paid APIs. Select up to {MAX_ENRICH} members, then run bulk enrich.
                Wrong details can be corrected on each candidate&apos;s profile.
              </Paragraph>
            </div>
            <Space wrap>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                disabled={selectedIds.length < 1}
                onClick={openEnrich}
              >
                Enrich selected ({selectedIds.length}/{MAX_ENRICH})
              </Button>
              <Popconfirm
                title="Delete this list only?"
                description="Candidate profiles in Firestore are not removed."
                onConfirm={() => onDeleteList(listId)}
              >
                <Button danger icon={<DeleteOutlined />}>Delete list</Button>
              </Popconfirm>
            </Space>
            <Card loading={detailLoading}>
              <Table
                rowKey="id"
                dataSource={members}
                rowSelection={rowSelection}
                pagination={false}
                scroll={{ x: 1250 }}
                columns={[
                  {
                    title: 'Name',
                    dataIndex: 'contactName',
                    render: (name, candidate) => {
                      if (candidate.missing) return '— missing';
                      return (
                        <Button
                          type="link"
                          style={{ padding: 0, height: 'auto', textAlign: 'left' }}
                          onClick={() => openCandidateProfile(candidate)}
                        >
                          {name || 'View candidate'}
                        </Button>
                      );
                    }
                  },
                  { title: 'Title', dataIndex: 'job_title' },
                  { title: 'Company', dataIndex: 'company' },
                  { title: 'Email', dataIndex: 'email' },
                  {
                    title: 'Actions',
                    key: 'actions',
                    width: 390,
                    render: (_, candidate) => (
                      <Space wrap onClick={(event) => event.stopPropagation()}>
                        <Button
                          icon={<MailOutlined />}
                          disabled={!candidate.email || candidate.missing}
                          onClick={() => setEmailCandidate(candidate)}
                        >
                          Email / send JD or resume
                        </Button>
                        <Button
                          icon={<PhoneOutlined />}
                          disabled={!candidate.phoneNumbers?.length || candidate.missing}
                          onClick={() => setCallCandidate(candidate)}
                        >
                          Call
                        </Button>
                        <Button
                          icon={<MessageOutlined />}
                          disabled={!candidate.phoneNumbers?.length || candidate.missing}
                          onClick={() => setWhatsappCandidate(candidate)}
                        >
                          WhatsApp
                        </Button>
                      </Space>
                    )
                  }
                ]}
              />
            </Card>
          </Space>

          <Modal
            title="Run bulk enrichment"
            open={enrichOpen}
            onCancel={() => !enrichLoading && setEnrichOpen(false)}
            footer={[
              <Button key="c" disabled={enrichLoading} onClick={() => setEnrichOpen(false)}>Cancel</Button>,
              <Button key="r" type="primary" loading={enrichLoading} onClick={runEnrich}>
                Start ({Math.min(selectedIds.length, MAX_ENRICH)})
              </Button>
            ]}
          >
            <Paragraph>
              This will enrich <strong>{Math.min(selectedIds.length, MAX_ENRICH)}</strong> profile(s).
              Open a candidate from Candidate Search to review or edit fields afterward.
            </Paragraph>
            {enrichResults?.results ? (
              <Table
                size="small"
                rowKey="candidate_id"
                dataSource={enrichResults.results}
                pagination={false}
                columns={[
                  { title: 'Candidate ID', dataIndex: 'candidate_id', ellipsis: true },
                  {
                    title: 'Status',
                    dataIndex: 'success',
                    render: (ok) => (ok ? <Text type="success">OK</Text> : <Text type="danger">Failed</Text>)
                  },
                  { title: 'Error', dataIndex: 'error', ellipsis: true }
                ]}
              />
            ) : null}
          </Modal>

          <SendCandidateEmailModal
            candidate={emailCandidate}
            isOpen={Boolean(emailCandidate)}
            onClose={() => setEmailCandidate(null)}
          />

          <SendCandidateCallModal
            visible={Boolean(callCandidate)}
            candidate={callCandidate}
            onCancel={() => setCallCandidate(null)}
          />

          <SendCandidateWhatsAppModal
            visible={Boolean(whatsappCandidate)}
            candidate={whatsappCandidate}
            onCancel={() => setWhatsappCandidate(null)}
            onSuccess={() => setWhatsappCandidate(null)}
          />
        </div>
      </AppLayout>
    );
  }

  // Index view (or detail loading)
  if (listId && !detail && detailLoading) {
    return (
      <AppLayout>
        <div className="py-8 px-4"><Card loading /></div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <Title level={2} style={{ marginBottom: 0 }}>Candidate lists</Title>
            <Text type="secondary">Curate tenant candidates for bulk enrichment (max {MAX_ENRICH} per run)</Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New list
          </Button>
        </div>
        <Card>
          <Table
            rowKey="id"
            loading={loading}
            dataSource={lists}
            onRow={(r) => ({ onClick: () => navigate(`/candidate-lists/${r.id}`), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Name', dataIndex: 'name' },
              { title: 'Members', dataIndex: 'member_count', width: 100 },
              {
                title: '',
                key: 'del',
                width: 80,
                render: (_, r) => (
                    <Popconfirm title="Delete list only?" onConfirm={() => onDeleteList(r.id)}>
                    <Button type="link" danger size="small" onClick={(e) => e.stopPropagation()}>Delete</Button>
                  </Popconfirm>
                )
              }
            ]}
          />
        </Card>
      </div>

      <Modal title="New candidate list" open={createOpen} onOk={onCreate} onCancel={() => setCreateOpen(false)} okText="Create">
        <Form form={createForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
}
